export const GUIDE_LAYOUT_VERSION = 4;
export const GUIDE_PAGE = Object.freeze({ width: 595.28, height: 841.89 });
export const GUIDE_COPY_HEIGHT = GUIDE_PAGE.height / 2;
export const GUIDE_PAPER_SIZES = Object.freeze({
  LEGAL: Object.freeze({ key: 'LEGAL', label: 'Legal', width: 612, height: 1008, widthMm: 216, heightMm: 356 }),
  OFICIO: Object.freeze({ key: 'OFICIO', label: 'Oficio', width: 612, height: 936, widthMm: 216, heightMm: 330 }),
  A4: Object.freeze({ key: 'A4', label: 'A4', width: GUIDE_PAGE.width, height: GUIDE_PAGE.height, widthMm: 210, heightMm: 297 }),
});
export const GUIDE_DEFAULT_PAPER_SIZE = 'LEGAL';

const inferGuidePaperSize = (page) => {
  const width = num(page?.width);
  const height = num(page?.height);
  if (!width || !height) return '';
  const candidates = Object.values(GUIDE_PAPER_SIZES);
  return candidates.reduce((best, candidate) => {
    const distance = Math.abs(width / height - candidate.width / candidate.height);
    return !best || distance < best.distance ? { key: candidate.key, distance } : best;
  }, null)?.key || '';
};

export function getGuidePaperSize(value = GUIDE_DEFAULT_PAPER_SIZE) {
  const key = typeof value === 'string' ? value : value?.paperSize;
  return GUIDE_PAPER_SIZES[key] || GUIDE_PAPER_SIZES[GUIDE_DEFAULT_PAPER_SIZE];
}

const LEGACY_RE_COORDS = {
  Logo: { x: 90, y: 30 }, NombreEmpresa: { x: 40, y: 120 }, PtoVenta: { x: 460, y: 63 },
  Numero: { x: 415, y: 63 }, TipoComprobante: { x: 460, y: 30 }, Fecha: { x: 470, y: 100 },
  Descripcion: { x: 50, y: 315 }, Observaciones: { x: 30, y: 1000 }, ValorD: { x: 600, y: 905 },
};

export const GUIDE_ZONES = Object.freeze([
  { key: 'header', y: 8, height: 76 },
  { key: 'parties', y: 84, height: 64 },
  { key: 'commercialCondition', y: 148, height: 20 },
  { key: 'items', y: 168, height: 120 },
  { key: 'observations', y: 288, height: 36 },
  { key: 'totals', y: 324, height: 50 },
  { key: 'receipt', y: 374, height: 31 },
  { key: 'legalFooter', y: 405, height: 12 },
]);

const DEFAULT_FIXED_TEXTS = {
  companyName: '', companyHeader: '', companyLegend: '', companyTax: '',
  footerLegal: '', bankData: '', receiptLegend: 'Recibí de conformidad el día ____ / ____ / ______',
  qrLegendTop: 'Seguimiento ONLINE de Envío', qrLegendMiddle: '', qrLegendBottom: '',
  placeholderLogo: '', documentTypeShort: 'GUÍA', documentTypeFull: 'GUÍA',
};

const ALL_KEYS = [
  'structure_top', 'structure_middle', 'structure_footer', 'header_logo', 'header_empresa',
  'header_datos_fiscales', 'header_qr', 'header_codigo_barra', 'header_numero_guia',
  'header_tipo_comprobante', 'header_fecha', 'header_punto_venta', 'recorrido', 'remitente',
  'destinatario', 'condicion_cobro', 'tipo_entrega', 'tabla_remitos', 'totales',
  'valor_declarado', 'observaciones', 'datos_bancarios', 'acuse_recibo', 'firma', 'footer_legal',
];
const DEFAULT_VISIBILITY = Object.fromEntries(ALL_KEYS.map((key) => [key, true]));
export const GUIDE_BLOCK_OPTIONS = [...ALL_KEYS];

const clone = (value) => JSON.parse(JSON.stringify(value));
export const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
export const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? fallback : value.toISOString();
  if (typeof value === 'object') return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};
const firstText = (...values) => values.map((value) => safeText(value)).find(Boolean) || '';
const parseRecord = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) return value;
  if (typeof value !== 'string' || !value.trim().startsWith('{')) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};
const slugify = (value, fallback = 'plantilla-guia') => firstText(value, fallback).toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;

export function formatGuideDate(value) {
  if (value === null || value === undefined || value === '') return '';
  let date;
  if (value instanceof Date) date = value;
  else if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
    date = new Date(Date.UTC(year, month - 1, day));
  } else date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function formatNumber(value, decimals = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return new Intl.NumberFormat('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(parsed);
}
export function formatMoney(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(parsed);
}

export function splitText(text, width, fontSize = 8, maxLines = 1) {
  const source = safeText(text);
  if (!source) return [];
  const capacity = Math.max(1, Math.floor(num(width, 1) / Math.max(num(fontSize, 8) * 0.56, 1)));
  const words = source.split(/\s+/);
  const lines = [];
  let line = '';
  for (const rawWord of words) {
    let word = rawWord;
    while (word.length > capacity) {
      if (line) { lines.push(line); line = ''; }
      lines.push(word.slice(0, capacity));
      word = word.slice(capacity);
    }
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= capacity) line = candidate;
    else { if (line) lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  const limit = Math.max(1, num(maxLines, 1));
  if (lines.length <= limit) return lines;
  const clipped = lines.slice(0, limit);
  clipped[limit - 1] = `${clipped[limit - 1].slice(0, Math.max(0, capacity - 1)).trimEnd()}…`;
  return clipped;
}

export function resolveGuideField(data, field) {
  if (!field) return '';
  return String(field).split('.').reduce((current, part) => current == null ? '' : current[part], data) ?? '';
}

function createBlock(key, label, kind, config = {}) {
  return { id: key, key, label, kind, layer: config.layer ?? 3, x: num(config.x), y: num(config.y),
    width: num(config.width), height: num(config.height), visible: config.visible !== false,
    align: config.align || 'left', fontSize: num(config.fontSize, 8), fontWeight: config.fontWeight || 'normal',
    rotation: num(config.rotation), opacity: config.opacity ?? 1, field: config.field || null,
    staticText: config.staticText || '', placeholder: config.placeholder || '', multiline: Boolean(config.multiline),
    lineHeight: num(config.lineHeight, num(config.fontSize, 8) + 2), shapes: config.shapes || [], columns: config.columns || [],
    rows: config.rows || [], meta: config.meta || {} };
}

const zoneRects = (keys) => GUIDE_ZONES.filter((zone) => keys.includes(zone.key)).map((zone) => ({
  type: 'rect', x: 10, y: zone.y, width: GUIDE_PAGE.width - 20, height: zone.height,
}));

function buildSystemBlocks() {
  return [
    createBlock('structure_top', 'Estructura superior', 'structure', { layer: 1, x: 10, y: 8, width: 575.28, height: 160, shapes: [
      ...zoneRects(['header', 'parties', 'commercialCondition']),
      { type: 'line', x1: 297.64, y1: 84, x2: 297.64, y2: 148 },
      { type: 'line', x1: 392, y1: 8, x2: 392, y2: 68 },
    ] }),
    createBlock('structure_middle', 'Estructura detalle', 'structure', { layer: 1, x: 10, y: 168, width: 575.28, height: 206, shapes: zoneRects(['items', 'observations', 'totals']) }),
    createBlock('structure_footer', 'Estructura pie', 'structure', { layer: 1, x: 10, y: 374, width: 575.28, height: 43, shapes: zoneRects(['receipt', 'legalFooter']) }),
    createBlock('header_logo', 'Logo', 'logo', { x: 16, y: 15, width: 70, height: 38, placeholder: DEFAULT_FIXED_TEXTS.placeholderLogo }),
    createBlock('header_empresa', 'Empresa', 'text', { x: 92, y: 23, width: 215, height: 14, fontSize: 11, fontWeight: 'bold', field: 'fixed.companyName', meta: { maxLines: 1 } }),
    createBlock('header_datos_fiscales', 'Datos fiscales', 'paragraph', { x: 92, y: 36, width: 215, height: 27, fontSize: 6.5, lineHeight: 8, rows: [
      { kind: 'dynamic', field: 'fixed.companyHeader' }, { kind: 'dynamic', field: 'fixed.companyLegend' }, { kind: 'dynamic', field: 'fixed.companyTax' },
    ] }),
    createBlock('header_qr', 'QR seguimiento', 'qr', { x: 324, y: 17, width: 42, height: 42, field: 'trackingUrl', meta: { label: 'Seguimiento ONLINE de Envío' } }),
    createBlock('header_tipo_comprobante', 'Tipo comprobante', 'text', { x: 400, y: 20, width: 177, height: 12, fontSize: 11, fontWeight: 'bold', align: 'center', field: 'tipoDocumento' }),
    createBlock('header_numero_guia', 'Número guía', 'text', { x: 400, y: 35, width: 177, height: 12, fontSize: 10, fontWeight: 'bold', align: 'center', field: 'guiaNumeroCompleto' }),
    createBlock('header_codigo_barra', 'Código de barra', 'barcode', { x: 418, y: 48, width: 141, height: 17, field: 'barcode' }),
    createBlock('header_fecha', 'Fecha', 'text', { x: 400, y: 73, width: 177, height: 9, fontSize: 7.5, align: 'right', field: 'fechaFormateada' }),
    createBlock('header_punto_venta', 'Punto de venta', 'text', { x: 400, y: 62, width: 177, height: 9, fontSize: 6.5, align: 'right', field: 'puntoVentaLabel' }),
    createBlock('recorrido', 'Origen y destino', 'text', { x: 18, y: 73, width: 365, height: 10, fontSize: 8.5, fontWeight: 'bold', align: 'center', field: 'recorrido', meta: { maxLines: 1 } }),
    createBlock('remitente', 'Remitente', 'party', { x: 18, y: 89, width: 271, height: 55, fontSize: 7.2, lineHeight: 8.5, meta: { role: 'remitente', title: 'REMITENTE', maxLines: 5 } }),
    createBlock('destinatario', 'Destinatario', 'party', { x: 306, y: 89, width: 271, height: 55, fontSize: 7.2, lineHeight: 8.5, meta: { role: 'destinatario', title: 'DESTINATARIO', maxLines: 5 } }),
    createBlock('condicion_cobro', 'Condición de venta', 'text', { x: 18, y: 160, width: 350, height: 9, fontSize: 7.5, fontWeight: 'bold', field: 'condicionCobro', meta: { maxLines: 1 } }),
    createBlock('tipo_entrega', 'Tipo de entrega', 'text', { x: 375, y: 160, width: 202, height: 9, fontSize: 7.5, fontWeight: 'bold', align: 'right', field: 'tipoEntregaLabel', meta: { maxLines: 1 } }),
    createBlock('tabla_remitos', 'Tabla de remitos', 'table', { x: 18, y: 174, width: 559, height: 108, fontSize: 6.2, meta: { rowHeight: 11, headerHeight: 14, maxRows: 8 }, columns: [
      { key: 'remito', label: 'Remito', x: 0, width: 72 }, { key: 'bultos', label: 'Bultos', x: 78, width: 32, align: 'right' },
      { key: 'descripcion', label: 'Descripción', x: 118, width: 150, maxLines: 2 }, { key: 'm3', label: 'M3', x: 274, width: 38, align: 'right' },
      { key: 'pesoAforo', label: 'Kg/Aforo', x: 320, width: 62, align: 'right' }, { key: 'concepto', label: 'Concepto', x: 390, width: 90 },
      { key: 'importe', label: 'Importe', x: 488, width: 70, align: 'right' },
    ] }),
    createBlock('observaciones', 'Observaciones', 'multiline', { x: 18, y: 298, width: 360, height: 22, fontSize: 6.8, lineHeight: 8, field: 'observacionesLabel', meta: { maxLines: 3 } }),
    createBlock('datos_bancarios', 'Datos bancarios', 'multiline', { x: 385, y: 298, width: 192, height: 22, fontSize: 6.2, lineHeight: 7.5, field: 'fixed.bankData', meta: { maxLines: 3 } }),
    createBlock('valor_declarado', 'Valor declarado', 'text', { x: 18, y: 337, width: 245, height: 9, fontSize: 7, field: 'valorDeclaradoLabel' }),
    createBlock('totales', 'Totales', 'totals', { x: 335, y: 329, width: 242, height: 42, fontSize: 7 }),
    createBlock('acuse_recibo', 'Acuse de recibo', 'text', { x: 18, y: 386, width: 245, height: 9, fontSize: 7, field: 'fixed.receiptLegend' }),
    createBlock('firma', 'Firma y aclaración', 'signature', { x: 270, y: 390, width: 307, height: 14, meta: { labels: [
      { text: 'DNI', x: 0, width: 72 }, { text: 'Aclaración', x: 87, width: 100 }, { text: 'Firma', x: 202, width: 105 },
    ] } }),
    createBlock('footer_legal', 'Pie legal', 'multiline', { x: 18, y: 413, width: 559, height: 7, fontSize: 5.2, lineHeight: 6, align: 'center', field: 'fixed.footerLegal', meta: { maxLines: 1 } }),
  ];
}

function buildOverlayBlocks() {
  return buildSystemBlocks().map((block) => block.key.startsWith('structure_') ? { ...block, visible: false, shapes: [] } : block);
}

export function parseLegacyReCoordinates(text) {
  const coords = clone(LEGACY_RE_COORDS);
  safeText(text).split(/\r?\n/).forEach((line) => {
    const match = line.match(/^([^:=]+)[:=](\d{1,4}),(\d{1,4})$/);
    if (match) coords[safeText(match[1])] = { x: num(match[2]), y: num(match[3]) };
  });
  return coords;
}

export function buildLegacyGuideTemplate(input = {}) {
  const modeImpresion = input.profile?.base === 'PAPELERIA_PREIMPRESA' || (input.profile?.base === 'PERSONALIZADA' && (input.profile.layoutMode || input.modeImpresion) === 'preprinted_overlay') || (!input.profile?.base && input.modeImpresion === 'preprinted_overlay') ? 'preprinted_overlay' : 'system_full';
  const presetLayout = ['clasica', 'compacta', 'operativa'].includes(input.presetLayout) ? input.presetLayout : 'clasica';
  const coords = parseLegacyReCoordinates(input.reText);
  const blocks = (modeImpresion === 'system_full' ? buildSystemBlocks() : buildOverlayBlocks()).map((block) => ({
    ...block, visible: input.visibilidadBloques?.[block.key] ?? block.visible,
  }));
  const profile = normalizeGuideProfile(input, modeImpresion);
  const page = getGuidePaperSize(profile);
  return {
    id: input.id || null, tenantId: input.tenantId || null,
    name: safeText(input.name && !/\b(TR|JT)\b/i.test(input.name) ? input.name : '', modeImpresion === 'system_full' ? 'Estándar Sinamon' : 'Papelería preimpresa'),
    slug: slugify(input.slug || input.name || (modeImpresion === 'system_full' ? 'estandar-sinamon' : 'papeleria-preimpresa')),
    active: input.active ?? true, status: safeText(input.status, input.active === false ? 'draft' : 'active'),
    modeImpresion, presetLayout, layoutVersion: GUIDE_LAYOUT_VERSION, pdfFondoUrl: safeText(input.pdfFondoUrl),
    logoUrl: safeText(input.logoUrl), usaLogo: input.usaLogo ?? true, mostrarPlaceholderLogo: input.mostrarPlaceholderLogo ?? true,
    textosFijos: { ...DEFAULT_FIXED_TEXTS, ...(input.textosFijos || {}),
      companyName: firstText(input.companyName, input.textosFijos?.companyName, DEFAULT_FIXED_TEXTS.companyName),
      companyHeader: firstText(input.companyHeader, input.textosFijos?.companyHeader),
      companyLegend: firstText(input.companyLegend, input.textosFijos?.companyLegend),
      documentTypeShort: modeImpresion === 'preprinted_overlay' ? 'GUÍA' : firstText(input.textosFijos?.documentTypeShort, 'GUÍA'),
      documentTypeFull: modeImpresion === 'preprinted_overlay' ? 'GUÍA' : firstText(input.textosFijos?.documentTypeFull, 'GUÍA'),
    },
    visibilidadBloques: { ...DEFAULT_VISIBILITY, ...(input.visibilidadBloques || {}) },
    globalOffsetX: num(input.globalOffsetX), globalOffsetY: num(input.globalOffsetY), zoomPreviewDefault: num(input.zoomPreviewDefault, 1),
    originBase: safeText(input.originBase, 'system'), versionNumber: num(input.versionNumber, GUIDE_LAYOUT_VERSION),
    profile, page: { width: page.width, height: page.height }, copy: { height: page.height / profile.copies, count: profile.copies }, zones: clone(GUIDE_ZONES), blocks,
    legacy: { coords }, createdAt: input.createdAt || null, updatedAt: input.updatedAt || null,
  };
}

export function normalizeGuideTemplate(raw = {}) {
  const base = buildLegacyGuideTemplate(raw);
  const storedBlocks = Array.isArray(raw.blocks) ? raw.blocks : [];
  if (!storedBlocks.length) return base;
  const inferredLegacyPage = !raw.page && storedBlocks.some((block) => num(block.y) + num(block.height) > GUIDE_COPY_HEIGHT)
    ? { width: 794, height: 1123 }
    : null;
  const baseByKey = new Map(base.blocks.map((block) => [block.key, block]));
  const blocks = storedBlocks.map((override) => {
    const current = baseByKey.get(override.key || override.id);
    if (!current) return { ...override, id: override.id || override.key, key: override.key || override.id,
      x: num(override.x), y: num(override.y), width: num(override.width), height: num(override.height),
      visible: override.visible !== false, layer: num(override.layer, 3), meta: override.meta || {}, columns: override.columns || [], shapes: override.shapes || [] };
    return { ...current, ...override, x: num(override.x, current.x), y: num(override.y, current.y), width: num(override.width, current.width),
      height: num(override.height, current.height), fontSize: num(override.fontSize, current.fontSize), rotation: num(override.rotation, current.rotation),
      layer: num(override.layer, current.layer), visible: override.visible ?? raw.visibilidadBloques?.[current.key] ?? current.visible,
      meta: { ...current.meta, ...(override.meta || {}) }, columns: Array.isArray(override.columns) && override.columns.length ? override.columns : current.columns,
      shapes: Array.isArray(override.shapes) ? override.shapes : current.shapes };
  }).filter(Boolean);
  const profile = normalizeGuideProfile({ ...raw, page: raw.page || inferredLegacyPage }, base.modeImpresion);
  const paper = getGuidePaperSize(profile);
  const page = { width: paper.width, height: paper.height };
  return { ...base, ...raw, layoutVersion: GUIDE_LAYOUT_VERSION, name: base.name, modeImpresion: base.modeImpresion, profile, page, copy: { height: page.height / profile.copies, count: profile.copies }, zones: raw.zones || base.zones,
    slug: slugify(raw.slug || raw.name || base.slug), textosFijos: { ...base.textosFijos, ...(raw.textosFijos || {}) },
    visibilidadBloques: { ...base.visibilidadBloques, ...(raw.visibilidadBloques || {}), ...Object.fromEntries(blocks.map((block) => [block.key, block.visible !== false])) }, blocks };
}

export function buildImportedTemplateFromLegacy(input = {}) {
  const legacyVariant = input.legacyVariant === 'JT' ? 'JT' : 'TR';
  return buildLegacyGuideTemplate({ ...input, name: input.name || 'Plantilla importada',
    slug: input.slug || 'plantilla-importada',
    modeImpresion: legacyVariant === 'JT' ? 'preprinted_overlay' : 'system_full', originBase: 'imported_from_vb' });
}

const readParty = (value, root = {}, snapshotValue = null) => {
  const party = parseRecord(value);
  const snapshot = parseRecord(snapshotValue);
  const address = party.domicilio && typeof party.domicilio === 'object' ? party.domicilio : {};
  const snapshotAddress = snapshot.domicilio && typeof snapshot.domicilio === 'object' ? snapshot.domicilio : {};
  const locality = party.localidad && typeof party.localidad === 'object' ? party.localidad : {};
  const snapshotLocality = snapshot.localidad && typeof snapshot.localidad === 'object' ? snapshot.localidad : {};
  return {
    nombre: firstText(party.razonSocial, party.razon_social, party.nombre, party.nombreFantasia,
      snapshot.razonSocial, snapshot.razon_social, snapshot.nombre, snapshot.nombreFantasia,
      typeof value === 'string' && !value.trim().startsWith('{') ? value : '', typeof snapshotValue === 'string' && !snapshotValue.trim().startsWith('{') ? snapshotValue : ''),
    direccion: firstText(party.direccion, typeof party.domicilio === 'string' ? party.domicilio : '', party.domicilioCompleto, party.domicilio_completo,
      [address.calle, address.numero || address.altura, address.pisoDepto].filter(Boolean).join(' '),
      snapshot.direccion, typeof snapshot.domicilio === 'string' ? snapshot.domicilio : '', snapshot.domicilioCompleto, snapshot.domicilio_completo,
      [snapshotAddress.calle, snapshotAddress.numero || snapshotAddress.altura, snapshotAddress.pisoDepto].filter(Boolean).join(' '), root.direccion),
    localidad: firstText(locality.nombre, party.localidadNombre, party.localidad_nombre, typeof party.localidad === 'string' ? party.localidad : '',
      snapshotLocality.nombre, snapshot.localidadNombre, snapshot.localidad_nombre, typeof snapshot.localidad === 'string' ? snapshot.localidad : '', root.localidad),
    telefono: firstText(party.telefono, party.celular, snapshot.telefono, snapshot.celular, root.telefono),
    documento: firstText(party.cuit, party.documento, party.dni, snapshot.cuit, snapshot.documento, snapshot.dni, root.documento),
    iva: firstText(party.condicionIVA, party.condicion_iva, party.iva, snapshot.condicionIVA, snapshot.condicion_iva, snapshot.iva, root.iva),
  };
};

const sum = (items, getter) => items.reduce((total, item) => total + num(getter(item)), 0);
const moneyValue = (...values) => {
  for (const value of values) if (value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))) return Number(value);
  return 0;
};

export function buildGuidePreviewData(payload = {}) {
  const remitosSource = Array.isArray(payload.remitos) ? payload.remitos : Array.isArray(payload.items) ? payload.items : [];
  const valuation = parseRecord(payload.valorizacion || payload.valorizacion_snapshot || payload.detalle_valorizacion_json);
  const concepts = valuation.items || valuation.conceptos || payload.conceptosValorizacion || payload.conceptos_valorizacion || payload.conceptos || [];
  const conceptRows = Array.isArray(concepts) ? concepts : [];
  const rows = remitosSource.map((row, index) => {
    const importe = moneyValue(row.importe, row.total, row.importe_total, row.valor_flete);
    return { id: row.id || `remito-${index + 1}`, remito: firstText(row.numero_remito, row.numeroRemito, row.numero),
      bultos: num(row.bultos, row.cantidad_bultos || 0) || '', descripcion: firstText(row.descripcion, row.detalle),
      m3: formatNumber(row.m3 ?? row.metros_cubicos, 3), kg: formatNumber(row.kg_reales ?? row.kgReales ?? row.peso, 2),
      aforo: formatNumber(row.kg_aforado ?? row.kgAforado ?? row.aforo, 2),
      pesoAforo: [formatNumber(row.kg_reales ?? row.kgReales ?? row.peso, 2), formatNumber(row.kg_aforado ?? row.kgAforado ?? row.aforo, 2)].filter(Boolean).join(' / '),
      concepto: firstText(row.concepto, row.concepto_nombre), cantidad: row.cantidad == null ? '' : formatNumber(row.cantidad), unidad: formatGuideEnum(row.unidad), valorDeclarado: row.valor_declarado == null ? '' : formatMoney(row.valor_declarado), observacion: firstText(row.observacion, row.observaciones),
      importeRaw: importe, importe: importe ? formatMoney(importe) : '' };
  });
  const normalizedConcepts = conceptRows.map((item) => ({ ...item, code: firstText(item.codigo, item.code, item.nombre).toUpperCase(), amount: num(item.importe ?? item.valor ?? item.total) }));
  const isInformative = (item) => item.informativo === true || item.code.includes('VALOR_DECLARADO') || item.code.includes('INFORMATIV');
  const isCod = (item) => item.code.includes('CONTRAREEMBOLSO') || item.code.includes('CONTRA_REEMBOLSO');
  const isTax = (item) => ['IVA', 'IIBB', 'IMPUEST'].some((token) => item.code.includes(token));
  const billableConceptTotal = sum(normalizedConcepts.filter((item) => !isInformative(item) && !isCod(item) && !isTax(item)), (item) => item.amount);
  const taxConceptTotal = sum(normalizedConcepts.filter(isTax), (item) => item.amount);
  const codConceptTotal = sum(normalizedConcepts.filter(isCod), (item) => item.amount);
  const rowTotal = sum(rows, (row) => row.importeRaw);
  const subtotal = moneyValue(payload.subtotal, payload.total_flete, payload.totalFlete, valuation.subtotal, valuation.total_flete, valuation.totalFlete,
    conceptRows.length ? billableConceptTotal : rowTotal);
  const taxes = moneyValue(payload.impuestos, payload.total_impuestos, payload.totalImpuestos, valuation.impuestos, valuation.total_impuestos, taxConceptTotal);
  const declared = moneyValue(payload.valor_declarado, payload.valorDeclarado);
  const cod = moneyValue(payload.contrareembolso, payload.contra_reembolso, payload.importe_contrareembolso, valuation.contrareembolso, codConceptTotal);
  const total = moneyValue(payload.total, payload.total_valorizado, payload.totalFinal, payload.total_final, valuation.totalFinal, valuation.total_final, valuation.total, subtotal + taxes + cod);
  const point = firstText(payload.punto_venta_codigo, payload.puntoVentaCodigo, payload.punto_venta_id, payload.puntoVentaId);
  const number = firstText(payload.numero_guia, payload.numeroGuia, payload.numero, payload.id);
  const origin = firstText(payload.origen?.nombre, payload.origen?.codigo, payload.sucursal_origen_nombre, payload.sucursalOrigenNombre, payload.localidad_origen);
  const destination = firstText(payload.destino?.nombre, payload.destino?.codigo, payload.sucursal_destino_nombre, payload.sucursalDestinoNombre, payload.localidad_destino);
  const guideNumber = [point ? point.padStart(4, '0') : '', number ? number.padStart(8, '0') : ''].filter(Boolean).join(' - ');
  const barcode = firstText(payload.barcode, `${point}${number}`.replace(/[^0-9A-Z]/gi, ''));
  return {
    guiaNumeroCompleto: guideNumber, puntoVenta: point, puntoVentaLabel: point ? `Punto de venta: ${point}` : '',
    fechaFormateada: formatGuideDate(payload.fecha_emision ?? payload.fechaEmision ?? payload.fecha), barcode,
    trackingUrl: firstText(payload.trackingUrl, payload.tracking_url),
    tipoDocumento: firstText(payload.tipo_documento, payload.tipoDocumento, payload.documento_titulo, 'GUÍA'),
    recorrido: origin || destination ? `${origin || 'ORIGEN'}  →  ${destination || 'DESTINO'}` : '',
    condicionCobro: firstText(payload.modalidad_cobro, payload.modalidadCobro, payload.formaPago, payload.condicion_venta) ? `Condición de venta: ${firstText(payload.modalidad_cobro, payload.modalidadCobro, payload.formaPago, payload.condicion_venta)}` : '',
    tipoEntregaLabel: firstText(payload.tipo_entrega, payload.tipoEntrega, payload.modalidad_entrega) ? `Entrega: ${firstText(payload.tipo_entrega, payload.tipoEntrega, payload.modalidad_entrega)}` : '',
    observaciones: firstText(payload.observaciones), observacionesLabel: firstText(payload.observaciones) ? `Observaciones: ${firstText(payload.observaciones)}` : '',
    valorDeclarado: declared, valorDeclaradoLabel: declared ? `Valor declarado: ${formatMoney(declared)}` : '',
    remitente: readParty(payload.remitente, {
      direccion: payload.remitenteDomicilio, localidad: payload.remitenteLocalidad, telefono: payload.remitenteTel, documento: payload.cuitRemitente, iva: payload.condicionIvaRemitente }, payload.remitente_snapshot ?? payload.remitenteSnapshot),
    destinatario: readParty(payload.destinatario, {
      direccion: payload.destinatarioDomicilio, localidad: payload.destinatarioLocalidad, telefono: payload.destinatarioTel, documento: payload.cuitDestinatario, iva: payload.condicionIvaDestinatario }, payload.destinatario_snapshot ?? payload.destinatarioSnapshot),
    remitos: rows,
    totals: { bultos: sum(remitosSource, (row) => row.bultos ?? row.cantidad_bultos) || '',
      kg: formatNumber(sum(remitosSource, (row) => row.kg_reales ?? row.kgReales ?? row.peso), 2),
      m3: formatNumber(sum(remitosSource, (row) => row.m3 ?? row.metros_cubicos), 3),
      aforo: formatNumber(sum(remitosSource, (row) => row.kg_aforado ?? row.kgAforado ?? row.aforo), 2),
      valorDeclarado: declared ? formatMoney(declared) : '', subtotal: subtotal ? formatMoney(subtotal) : '',
      contrareembolso: cod ? formatMoney(cod) : '', impuestos: taxes ? formatMoney(taxes) : '', total: formatMoney(total) },
  };
}

export const GUIDE_TEMPLATE_SAMPLE = {
  id: 1042, numero_guia: '1042', punto_venta_id: '1', fecha_emision: '2026-09-17',
  modalidad_cobro: 'Cuenta corriente destino', tipo_entrega: 'Sucursal', origen: { nombre: 'Rosario' }, destino: { nombre: 'Corrientes' },
  observaciones: 'Entregar con control de sello, firma y aclaración.', valor_declarado: 150000, total: 182500, contrareembolso: 25000,
  remitente: { nombre: 'Remitente de ejemplo', direccion: 'Av. Ovidio Lagos 2450', localidad: 'Rosario', telefono: '341-555-0101', documento: '30-71234567-8', iva: 'Resp. Inscripto' },
  destinatario: { nombre: 'Destinatario de ejemplo', direccion: 'Ruta 11 km 102', localidad: 'Corrientes Capital', telefono: '379-555-0101', documento: '30-70999888-1', iva: 'Resp. Inscripto' },
  remitos: [{ id: 'r1', numero_remito: '0001-00000321', descripcion: 'Bultos varios', bultos: 4, kg_reales: 125.4, m3: 0.9, kg_aforado: 140, concepto: 'Flete', importe: 120000 }],
};

export function getGuideCopyOffsets(template) {
  const profile = normalizeGuideProfile(template || {}, template?.modeImpresion);
  const pageHeight = num(template?.page?.height, getGuidePaperSize(profile).height);
  return profile.copies === 1 ? [0] : [0, pageHeight / 2];
}

export const GUIDE_PROFILE_BASES = ['ESTANDAR_SINAMON', 'PAPELERIA_PREIMPRESA', 'PERSONALIZADA'];
export const GUIDE_PARTY_FIELDS = ['nombre', 'direccion', 'localidad', 'telefono', 'documento', 'iva'];
export const GUIDE_COLUMN_DEFINITIONS = [
  { key: 'remito', label: 'Remito', width: 24 }, { key: 'bultos', label: 'Bultos', width: 12 },
  { key: 'descripcion', label: 'Descripción', width: 32 }, { key: 'm3', label: 'M3', width: 10 },
  { key: 'pesoAforo', label: 'Kg/Aforo', width: 22 }, { key: 'kg', label: 'Kilos', width: 12 },
  { key: 'aforo', label: 'Aforo', width: 12 }, { key: 'cantidad', label: 'Cantidad', width: 10 },
  { key: 'unidad', label: 'Unidad', width: 10 }, { key: 'valorDeclarado', label: 'Valor declarado', width: 16 },
  { key: 'observacion', label: 'Observación', width: 24 },
];
export const GUIDE_PROFILE_REGIONS = Object.fromEntries(Object.entries({
  header: { y: 8, height: 76 }, parties: { y: 84, height: 64 }, commercial: { y: 148, height: 24 },
  detail: { y: 172, height: 116 }, concepts: { y: 324, height: 48 }, totals: { y: 324, height: 48 },
  notes: { y: 288, height: 36 }, receipt: { y: 374, height: 27 }, legal: { y: 401, height: 18 },
}).map(([key, region]) => [key, { ...region, x: key === 'totals' ? 297.64 : 18, width: ['concepts', 'totals'].includes(key) ? 279.64 : 559.28 }]));
const REGION_BLOCKS = { header: 'header_logo', parties: 'remitente', commercial: 'condicion_cobro', detail: 'tabla_remitos',
  concepts: 'totales', totals: 'totales', notes: 'observaciones', receipt: 'acuse_recibo', legal: 'footer_legal' };
const PROFILE_BLOCKS = {
  showLogo: 'header_logo', showIdentity: 'header_empresa', showQr: 'header_qr',
  showBarcode: 'header_codigo_barra', showDate: 'header_fecha', showRoute: 'recorrido',
  showValuation: 'totales', showDeclaredValue: 'valor_declarado', showBanking: 'datos_bancarios',
  showObservations: 'observaciones', showReceipt: 'acuse_recibo', showLegal: 'footer_legal',
};
const bounded = (value, fallback, min, max) => Math.max(min, Math.min(max, num(value, fallback)));

export function normalizeGuideProfile(raw = {}, mode = 'system_full') {
  const source = raw.profile && typeof raw.profile === 'object' ? raw.profile : raw;
  const overlay = source.base === 'PAPELERIA_PREIMPRESA' || (source.base === 'PERSONALIZADA' && (source.layoutMode || mode) === 'preprinted_overlay') || (!source.base && mode === 'preprinted_overlay');
  const visibility = raw.visibilidadBloques || {};
  const blockVisible = (key) => raw.blocks?.find((block) => block.key === key)?.visible ?? visibility[key];
  const profile = {
    base: GUIDE_PROFILE_BASES.includes(source.base) ? source.base : overlay ? 'PAPELERIA_PREIMPRESA' : 'ESTANDAR_SINAMON',
    layoutMode: overlay ? 'preprinted_overlay' : 'system_full',
    copies: Number(source.copies ?? raw.copy?.count) === 1 ? 1 : 2,
    paperSize: GUIDE_PAPER_SIZES[source.paperSize]
      ? source.paperSize
      : inferGuidePaperSize(raw.page)
        || ((num(raw.layoutVersion) > 0 && num(raw.layoutVersion) < GUIDE_LAYOUT_VERSION) || (!raw.profile && Array.isArray(raw.blocks) && raw.blocks.length) ? 'A4' : GUIDE_DEFAULT_PAPER_SIZE),
    showLogo: !overlay, showIdentity: !overlay, showQr: true, showBarcode: true,
    showDate: true, showRoute: true, showValuation: true, showDeclaredValue: true,
    showCod: true, showTaxes: true, showBanking: !overlay, showObservations: true,
    showReceipt: !overlay, showLegal: !overlay, borders: !overlay, showTitles: !overlay,
    fontSize: bounded(source.fontSize, 8, 7.5, 12),
    trackingUrlTemplate: safeText(source.trackingUrlTemplate),
    offsetX: bounded(source.offsetX ?? raw.globalOffsetX, 0, -100, 100),
    offsetY: bounded(source.offsetY ?? raw.globalOffsetY, 0, -100, 100),
    bankData: safeText(source.bankData ?? (raw.originBase === 'tenant_custom' ? raw.textosFijos?.bankData : '')),
    legalText: safeText(source.legalText ?? (raw.originBase === 'tenant_custom' ? raw.textosFijos?.footerLegal : '')),
  };
  for (const key of Object.keys(profile)) {
    if (typeof profile[key] !== 'boolean') continue;
    const configured = typeof source[key] === 'boolean'
      ? source[key]
      : key === 'showLogo' && typeof raw.usaLogo === 'boolean'
        ? raw.usaLogo
        : undefined;
    const legacy = PROFILE_BLOCKS[key] ? blockVisible(PROFILE_BLOCKS[key]) : undefined;
    profile[key] = typeof configured === 'boolean' ? configured : !overlay && typeof legacy === 'boolean' ? legacy : profile[key];
  }
  // Identity, headings and rules are already present on preprinted stock.

  for (const key of ['senderFields', 'recipientFields']) {
    profile[key] = Array.isArray(source[key])
      ? [...new Set(source[key].filter((field) => GUIDE_PARTY_FIELDS.includes(field)))]
      : [...GUIDE_PARTY_FIELDS];
  }
  const legacyColumns = raw.blocks?.find((block) => block.key === 'tabla_remitos')?.columns;
  const columns = Array.isArray(source.columns) ? source.columns : legacyColumns;
  const seen = new Set();
  profile.columns = (Array.isArray(columns) ? columns : GUIDE_COLUMN_DEFINITIONS.map((column, index) => ({ ...column, visible: index < 5 })))
    .filter((column) => column && GUIDE_COLUMN_DEFINITIONS.some((item) => item.key === column.key) && !seen.has(column.key) && seen.add(column.key))
    .map((column) => {
      const base = GUIDE_COLUMN_DEFINITIONS.find((item) => item.key === column.key);
      return { key: base.key, label: base.label, width: bounded(column.width, base.width, 1, 1000), visible: column.visible !== false };
    });
  for (const column of GUIDE_COLUMN_DEFINITIONS) if (!seen.has(column.key)) profile.columns.push({ ...column, visible: false });
  const standardBlocks = buildSystemBlocks();
  profile.regions = Object.fromEntries(Object.entries(GUIDE_PROFILE_REGIONS).map(([key, defaults]) => {
    const configured = source.regions?.[key];
    const block = raw.blocks?.find((item) => item.key === REGION_BLOCKS[key]);
    const standard = standardBlocks.find((item) => item.key === REGION_BLOCKS[key]);
    const scale = raw.page?.height ? GUIDE_PAGE.height / raw.page.height : 1;
    const migratedY = block && standard ? defaults.y + num(block.y) * scale - standard.y : defaults.y;
    const scaleX = raw.page?.width ? GUIDE_PAGE.width / raw.page.width : 1;
    const migratedX = block && standard ? defaults.x + num(block.x) * scaleX - standard.x : defaults.x;
    const migratedWidth = block && standard ? defaults.width + num(block.width, standard.width) * scaleX - standard.width : defaults.width;
    const migratedHeight = block && standard ? defaults.height + num(block.height, standard.height) * scale - standard.height : defaults.height;
    return [key, { x: num(configured?.x, migratedX), width: num(configured?.width, migratedWidth), y: num(configured?.y, migratedY), height: num(configured?.height, migratedHeight) }];
  }));
  return profile;
}

export function validateGuideProfile(profile) {
  if (profile.trackingUrlTemplate) {
    try {
      const template = profile.trackingUrlTemplate;
      if (template.length > 2048 || /\{(?!barcode\}|guideNumber\}|guideId\})/.test(template)) throw new Error('Invalid tracking template');
      const url = new URL(template.replace(/\{(barcode|guideNumber|guideId)\}/g, 'sample'));
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid tracking protocol');
    } catch {
      return ['La URL de seguimiento debe ser HTTP(S), hasta 2048 caracteres y sólo admite {barcode}, {guideNumber} o {guideId}.'];
    }
  }
  const paper = getGuidePaperSize(profile);
  const limit = paper.height / profile.copies;
  if (Math.abs(profile.offsetX) > 15 || Math.abs(profile.offsetY) > 15) return ['El desplazamiento máximo es 15 puntos. Para cambios mayores ajustá las áreas del papel preimpreso.'];
  if (Object.values(profile.regions || {}).some((region) => !['x', 'y', 'width', 'height'].every((key) => Number.isFinite(region[key])) || region.y < 0 || region.height < 1 || region.y + region.height > limit || region.x < 0 || region.width < 20 || region.x + region.width > paper.width)) return ['Las áreas de impresión deben permanecer dentro de cada copia de la hoja.'];
  if (profile.layoutMode === 'preprinted_overlay') {
    const regions = Object.entries(profile.regions || {});
    for (let i = 0; i < regions.length; i++) for (let j = i + 1; j < regions.length; j++) {
      const [nameA, a] = regions[i]; const [nameB, b] = regions[j];
      const overlaps = a.x < b.x + b.width - 0.01 && a.x + a.width > b.x + 0.01 && a.y < b.y + b.height - 0.01 && a.y + a.height > b.y + 0.01;
      if (overlaps) return ['Las áreas ' + nameA + ' y ' + nameB + ' se superponen. Ajustá sus posiciones antes de guardar.'];
    }
  }
  if (profile.bankData.length > 2000 || profile.legalText.length > 3000) return ['Los datos bancarios admiten hasta 2000 caracteres y el pie legal hasta 3000.'];
  if (!profile.columns.some((column) => column.visible)) return ['Seleccioná al menos una columna de remitos.'];
  return [];
}

export function formatGuideEnum(value) {
  const raw = safeText(value);
  const labels = {
    cta_cte_destino: 'Cuenta corriente - paga destino', cta_cte_origen: 'Cuenta corriente - paga origen',
    cuenta_corriente: 'Cuenta corriente', contado_origen: 'Contado - paga origen',
    contado_destino: 'Contado - paga destino', contado: 'Contado', domicilio: 'Domicilio',
    sucursal: 'Sucursal', retiro_sucursal: 'Retiro en sucursal', puerta_puerta: 'Puerta a puerta',
    responsable_inscripto: 'Responsable inscripto', consumidor_final: 'Consumidor final',
    monotributista: 'Monotributista', exento: 'Exento',
  };
  return labels[raw.toLowerCase()] || raw.replace(/_/g, ' ').replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}

export function formatGuideDocument(value) {
  const raw = safeText(value);
  const digits = raw.replace(/\D/g, '');
  return digits.length === 11 ? digits.slice(0, 2) + '-' + digits.slice(2, 10) + '-' + digits.slice(10) : raw;
}

export function buildGuidePrintViewModel(payload = {}) {
  const data = buildGuidePreviewData(payload);
  for (const party of [data.remitente, data.destinatario]) {
    party.documento = formatGuideDocument(party.documento);
    party.iva = formatGuideEnum(party.iva);
  }
  data.condicionCobro = formatGuideEnum(firstText(payload.modalidad_cobro, payload.modalidadCobro, payload.formaPago, payload.condicion_venta));
  data.tipoEntregaLabel = formatGuideEnum(firstText(payload.tipo_entrega, payload.tipoEntrega, payload.modalidad_entrega));
  const valuation = parseRecord(payload.valorizacion || payload.valorizacion_snapshot || payload.detalle_valorizacion_json);
  const source = valuation.items || valuation.conceptos || payload.conceptosValorizacion || payload.conceptos_valorizacion || payload.conceptos || [];
  data.concepts = (Array.isArray(source) ? source : []).filter((item) => item && item.informativo !== true).map((item) => {
    const amount = moneyValue(item.importe, item.total, item.valor);
    const code = firstText(item.codigo, item.code, item.nombre).toUpperCase();
    const kind = code.includes('CONTRAREEMBOLSO') || code.includes('CONTRA_REEMBOLSO')
      ? 'cod'
      : ['IVA', 'IIBB', 'IMPUEST'].some((token) => code.includes(token))
        ? 'tax'
        : 'service';
    const explicitLabel = firstText(item.nombre, item.descripcion, item.concepto);
    return { label: explicitLabel || formatGuideEnum(firstText(item.codigo, item.code)), code, kind, amount, formattedAmount: formatMoney(amount) };
  });
  data.company = parseRecord(payload.company || payload.empresa);
  if (data.company.cuit) data.company.cuit = formatGuideDocument(data.company.cuit);
  data.document = { number: data.guiaNumeroCompleto, date: data.fechaFormateada, type: data.tipoDocumento, barcode: data.barcode };
  data.tracking = { url: data.trackingUrl, barcode: data.barcode };
  data.sender = data.remitente;
  data.recipient = data.destinatario;
  data.shipment = { route: data.recorrido, paymentCondition: data.condicionCobro, deliveryType: data.tipoEntregaLabel, remitos: data.remitos, declaredValue: data.valorDeclarado };
  data.banking = { text: safeText(payload.bankData) };
  data.footer = { legalText: safeText(payload.legalText), observations: data.observaciones };
  return data;
}